(() => {
  let cleanup;
  const initialize = () => {
    cleanup?.();
    const controller = new AbortController();
    const timers = new Set();
    const observers = [];
    const listen = (target, event, handler, options = {}) => target?.addEventListener(event, handler, {...options, signal: controller.signal});
    const repeat = (handler, delay) => { const timer = setInterval(handler, delay); timers.add(timer); return timer; };
    const observe = (...args) => { const observer = new IntersectionObserver(...args); observers.push(observer); return observer; };
    cleanup = () => { const openDialog = document.querySelector('[data-sample-book][open]'); if (openDialog) openDialog.close(); controller.abort(); timers.forEach(clearInterval); observers.forEach(observer => observer.disconnect()); };

  const dialog = document.querySelector('[data-sample-book]');
  const triggers = [...document.querySelectorAll('[data-open-sample-book], .mini-book-actions a[href*="warehouse-h-sample-pages"], .book-band .book-actions a[href*="warehouse-h-sample-pages"], .collection-intro a[href*="warehouse-h-sample-pages"]')];
  triggers.forEach(trigger => {
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', 'sample-book-dialog');
  });
  if (!dialog || !triggers.length || !dialog.showModal) return;
  const pages = JSON.parse(dialog.querySelector('[data-sample-pages]').textContent);
  const spread = dialog.querySelector('[data-sample-spread]');
  const stage = dialog.querySelector('[data-sample-stage]');
  const status = dialog.querySelector('[data-sample-status]');
  const transcript = dialog.querySelector('[data-sample-transcript]');
  const previous = dialog.querySelector('[data-sample-previous]');
  const next = dialog.querySelector('[data-sample-next]');
  const picker = dialog.querySelector('[data-sample-picker]');
  const zoom = dialog.querySelector('[data-sample-zoom]');
  const narrow = matchMedia('(max-width: 760px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const images = new Map();
  let current = 0;
  let changing = false;
  let generation = 0;
  let opener;
  let touch;
  let activeAnimation;
  let previousOverflow;
  const label = (template, first, last = first) => template.replaceAll('{first}', first).replaceAll('{last}', last).replaceAll('{total}', pages.length);
  const normalize = index => narrow.matches || index === 0 ? index : index % 2 === 0 ? index - 1 : index;
  const visible = index => narrow.matches || index === 0 ? [index] : [index, index + 1].filter(i => i < pages.length);
  const destination = direction => {
    if (narrow.matches) return current + direction;
    if (direction < 0) return current === 1 ? 0 : current - 2;
    return current === 0 ? 1 : current + 2;
  };
  function load(index) {
    if (!pages[index]) return Promise.resolve();
    if (!images.has(index)) {
      const image = new Image();
      const promise = new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => { images.delete(index); reject(new Error('page')); };
      });
      image.src = pages[index].src;
      images.set(index, promise);
    }
    return images.get(index);
  }
  function paper(index, side) {
    const wrapper = document.createElement('div');
    wrapper.className = 'sample-paper sample-paper-' + side;
    if (index === undefined) { wrapper.classList.add('sample-paper-empty'); return wrapper; }
    const page = pages[index];
    const image = document.createElement('img');
    image.src = page.src;
    image.width = page.width;
    image.height = page.height;
    image.alt = label(dialog.dataset.pageAlt, page.number);
    image.draggable = false;
    wrapper.append(image);
    return wrapper;
  }
  function render() {
    const indices = visible(current);
    const single = indices.length === 1;
    spread.classList.toggle('sample-single', single);
    spread.classList.toggle('sample-cover', current === 0);
    spread.replaceChildren(...indices.map((index, i) => paper(index, single ? 'single' : i === 0 ? 'left' : 'right')));
    const first = pages[indices[0]].number;
    const last = pages[indices.at(-1)].number;
    status.textContent = label(single ? dialog.dataset.singleLabel : dialog.dataset.pairLabel, first, last);
    transcript.textContent = indices.map(i => pages[i].text).join('\n\n');
    previous.disabled = current === 0;
    next.disabled = destination(1) >= pages.length;
    picker.value = String(current);
    stage.scrollTo({top: 0, left: 0});
    for (const index of [destination(1), destination(1) + 1]) load(index).catch(() => {});
  }
  async function go(index, animate = true) {
    if (changing || index < 0 || index >= pages.length) return;
    const target = normalize(index);
    if (target === current && spread.childElementCount) return;
    changing = true;
    const token = ++generation;
    dialog.setAttribute('aria-busy', 'true');
    const old = current;
    const outgoing = spread.querySelector(target > old ? '.sample-paper:last-child' : '.sample-paper:first-child');
    const sheet = outgoing?.cloneNode(true);
    try {
      await Promise.all(visible(target).map(load));
      if (token !== generation || !dialog.open) return;
      current = target;
      render();
      if (animate && sheet && !reduced.matches && !stage.classList.contains('is-zoomed') && old !== 0 && current !== 0) {
        sheet.className = 'sample-turning-page';
        sheet.setAttribute('aria-hidden', 'true');
        const forward = target > old;
        sheet.style.left = narrow.matches || !forward ? '0' : '50%';
        sheet.style.width = narrow.matches ? '100%' : '50%';
        sheet.style.transformOrigin = forward ? 'left center' : 'right center';
        spread.append(sheet);
        activeAnimation = sheet.animate([{transform:'rotateY(0deg)',opacity:1},{transform:'rotateY(' + (forward ? '-155' : '155') + 'deg)',opacity:0}], {duration:420,easing:'cubic-bezier(.25,.65,.3,1)'});
        await activeAnimation.finished.catch(() => {});
        sheet.remove();
        activeAnimation = undefined;
      }
    } catch {
      status.textContent = dialog.dataset.errorLabel;
    } finally {
      if (token === generation) { changing = false; dialog.removeAttribute('aria-busy'); }
    }
  }
  function setZoom(enabled) {
    stage.classList.toggle('is-zoomed', enabled);
    zoom.setAttribute('aria-pressed', String(enabled));
    zoom.textContent = enabled ? dialog.dataset.zoomOutLabel : dialog.dataset.zoomInLabel;
    stage.scrollTo({top: 0, left: 0});
  }
  function open(event) {
    event.preventDefault();
    opener = event.currentTarget;
    if (dialog.open) return;
    previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    if (!picker.options.length) pages.forEach((page, index) => picker.add(new Option(label(dialog.dataset.jumpLabel, page.number), String(index))));
    go(current, false);
  }
  triggers.forEach(trigger => listen(trigger, 'click', open));
  listen(previous, 'click', () => go(destination(-1)));
  listen(next, 'click', () => go(destination(1)));
  listen(picker, 'change', () => go(Number(picker.value)));
  listen(zoom, 'click', () => setZoom(zoom.getAttribute('aria-pressed') !== 'true'));
  listen(dialog.querySelector('[data-close-sample-book]'), 'click', () => dialog.close());
  listen(dialog, 'click', event => { if (event.target === dialog) dialog.close(); });
  listen(dialog, 'close', () => {
    generation++;
    changing = false;
    activeAnimation?.cancel();
    spread.querySelectorAll('.sample-turning-page').forEach(page => page.remove());
    dialog.removeAttribute('aria-busy');
    document.documentElement.style.overflow = previousOverflow;
    setZoom(false);
    opener?.focus();
  });
  listen(dialog, 'keydown', event => {
    if (event.target.closest('select,input,textarea')) return;
    const index = event.key === 'ArrowRight' ? destination(1) : event.key === 'ArrowLeft' ? destination(-1) : event.key === 'Home' ? 0 : event.key === 'End' ? pages.length - 1 : undefined;
    if (index !== undefined) { event.preventDefault(); go(index); }
  });
  listen(stage, 'touchstart', event => { touch = event.touches.length === 1 ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : undefined; }, {passive:true});
  listen(stage, 'touchend', event => {
    if (!touch || stage.classList.contains('is-zoomed')) return;
    const dx = event.changedTouches[0].clientX - touch.x;
    const dy = event.changedTouches[0].clientY - touch.y;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.4) go(destination(dx < 0 ? 1 : -1));
    touch = undefined;
  }, {passive:true});
  listen(narrow, 'change', () => { if (dialog.open) { activeAnimation?.cancel(); current = normalize(current); render(); } });

  };
  initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', () => cleanup?.());
})();
